import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import CustomSelect from "../../../../../../components/FormComponents/CustomSelect";
import Button from "react-bootstrap/Button";
import {useEffect, useState} from "react";
import {IDataRowMultiChoiceField, IDataRowMultiChoiceOption} from "../../../../../../core/interfaces/document";
import {
    DataRowMultiChoiceType,
    DataRowType,
    MultiChoiceAlternativeClassified,
    MultiChoiceDisplayType
} from "../../../../../../core/enums/enums";
import {
    useDeleteDataRowMultiChoiceOption,
    useGetMultiChoiceOptions
} from "../../../../../../api/DocumentModuleChecklistApi";
import When from "../../../../../../components/When";
import {Form, Table} from "react-bootstrap";
import {addToast} from "../../../../../../store/slices/uiSlice";
import {toastError} from "../../../../../../core/helpers/toastHelper";
import {useAppDispatch} from "../../../../../../store/hooks";
import _ from "lodash";
import CustomTextInput from "../../../../../../components/FormComponents/CustomTextInput";
import CreateNewDataRowMultiChoiceOption from "./CreateNewDataRowMultiChoiceOption";
import SimpleConfirmModal from "../../../../../../components/ModalComponents/SimpleConfirmModal";

const newDataRowMultiChoice: IDataRowMultiChoiceField = {
    checklistFormDataRowTypeSymbol: DataRowType.MULTI_CHOICE_FIELD,
    isNew: true,
    header: "",
    isClosed: false,
}

interface IAddMultiChoice {
    multiChoiceType: DataRowMultiChoiceType;
    onAdd: (dataRow: IDataRowMultiChoiceField) => void;
    onCancel: () => void;
}

const AddMultiChoice = (
    {
        multiChoiceType,
        onAdd,
        onCancel,
    }: IAddMultiChoice) => {
    const dispatch = useAppDispatch();

    const [dataRow, setDataRow] = useState(newDataRowMultiChoice);
    const [multiChoiceOptions, setMultiChoiceOptions] = useState<IDataRowMultiChoiceOption[]>([]);
    const [showCreateNewMultiChoiceOptionModal, setShowCreateNewMultiChoiceOptionModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState<IDataRowMultiChoiceOption | null>(null);

    const {
        data: multiChoicesResponse,
    } = useGetMultiChoiceOptions(multiChoiceType);

    useEffect(() => {
        setMultiChoiceOptions(multiChoicesResponse?.data.data ?? []);
    }, [multiChoicesResponse?.data.data]);

    const handleOnAdd = () => {
        onAdd(dataRow);
    }

    const [showDeleteOptionModal, setShowDeleteOptionModal] = useState(false);
    const [deleteOption] = useDeleteDataRowMultiChoiceOption(selectedOption?.id ?? "");
    const handleDeleteOption = async () => {
        setShowDeleteOptionModal(false);
        if (!selectedOption) {
            dispatch(addToast(toastError("Ett fel uppstod.", "Alternativet kunde inte raderas. Försök igen eller kontakta support.")));
            return;
        }
        await deleteOption(selectedOption.placeholder).then((response) => {
            if (response.status === 202) {
                setDataRow({
                    ...dataRow,
                    multiChoiceAlternatives: undefined
                });
                setSelectedOption(null);
                return;
            }
        });
    }

    const onChangeOption = (e: any) => {
        if (e.target.value === "") {
            setDataRow({
                ...dataRow,
                multiChoiceAlternatives: undefined
            });
            setSelectedOption(null);
            return;
        }
        const multiChoiceOptionsClone = _.cloneDeep(multiChoiceOptions);
        const selectedIndex = multiChoiceOptionsClone.findIndex(opt => opt.id === e.target.value);
        if (selectedIndex < 0) {
            dispatch(addToast(toastError("Ett fel uppstod", "Kunde inte välja alternativ. Vänligen försök igen")));
            return;
        }
        const option = multiChoiceOptionsClone[selectedIndex];
        option.alternatives.forEach((opt) => opt.classifiedAs = MultiChoiceAlternativeClassified.TRANSPARENT);
        setDataRow({
            ...dataRow,
            multiChoiceAlternatives: option.alternatives,
        });
        setSelectedOption(option);
    }

    const renderMultiChoiceOptions = () => {
        if (multiChoiceOptions.length === 0) return <option value={""}>Inga val</option>
        return <>
            <option value={""}>Välj här</option>
            {multiChoiceOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.placeholder}</option>
            ))}
        </>
    }

    const handleOnChange = (e: any, alternativeName: string) => {
        if (!dataRow.multiChoiceAlternatives) {
            dispatch(addToast(toastError("Ett fel uppstod", "Kunde inte välja alternativ. Vänligen försök igen")));
            return;
        }
        const alternativeIndex = dataRow.multiChoiceAlternatives.findIndex((obj) => obj.name === alternativeName);
        if (alternativeIndex < 0) {
            dispatch(addToast(toastError("Ett fel uppstod", "Kunde inte välja alternativ. Vänligen försök igen")));
            return;
        }
        dataRow.multiChoiceAlternatives[alternativeIndex].classifiedAs = e.target.value;
        setDataRow({...dataRow});
    }

    const onChange = (e: any) => {
        setDataRow({
            ...dataRow,
            [e.target.name]: e.target.value,
        })
    }

    const colorPositive = "rgb(0, 128, 0)";
    const colorNegative = "rgb(255, 0, 0)";
    const colorNeutral = "rgb(245,165,0)";
    const colorTransparent = "";

    const renderValues = () => {
        if (!dataRow.multiChoiceAlternatives) return <></>
        return dataRow.multiChoiceAlternatives.map((alternative) => {
            return <tr key={alternative.name}>
                <td>{alternative.name}</td>
                <td>
                    <Form.Check
                        style={{color: colorPositive}}
                        type={"radio"}
                        inline
                        label={"Positiv"}
                        checked={MultiChoiceAlternativeClassified.POSITIVE === alternative.classifiedAs}
                        name={alternative.name}
                        id={"positive_" + alternative.name}
                        value={MultiChoiceAlternativeClassified.POSITIVE}
                        onChange={(e: any) => handleOnChange(e, alternative.name)}
                    />
                    <Form.Check
                        style={{color: colorNegative}}
                        type={"radio"}
                        inline
                        label={"Negativ"}
                        name={alternative.name}
                        id={"negative_" + alternative.name}
                        checked={MultiChoiceAlternativeClassified.NEGATIVE === alternative.classifiedAs}
                        value={MultiChoiceAlternativeClassified.NEGATIVE}
                        onChange={(e: any) => handleOnChange(e, alternative.name)}
                    />
                    <Form.Check
                        style={{color: colorNeutral}}
                        type={"radio"}
                        inline
                        label={"Neutral"}
                        name={alternative.name}
                        id={"neutral_" + alternative.name}
                        checked={MultiChoiceAlternativeClassified.NEUTRAL === alternative.classifiedAs}
                        value={MultiChoiceAlternativeClassified.NEUTRAL}
                        onChange={(e: any) => handleOnChange(e, alternative.name)}
                    />
                    <Form.Check
                        style={{color: colorTransparent}}
                        type={"radio"}
                        inline
                        label={"Transparent"}
                        name={alternative.name}
                        id={"transparent_" + alternative.name}
                        checked={MultiChoiceAlternativeClassified.TRANSPARENT === alternative.classifiedAs}
                        value={MultiChoiceAlternativeClassified.TRANSPARENT}
                        onChange={(e: any) => handleOnChange(e, alternative.name)}
                    />
                </td>
            </tr>
        });
    }

    return <>
        <Row>
            <Col md={3}>Rubrik</Col>
            <Col md={4}>
                <CustomTextInput
                    name={"header"}
                    value={dataRow.header}
                    onChange={onChange}
                    disableAutocomplete
                    required
                    autofocus={true}
                />
            </Col>
        </Row>

        <Row className={"mt-3"}>
            <Col md={3}>Typ av innehåll</Col>
            <Col md={4}>
                <CustomSelect
                    isEditMode={true}
                    name={"option"}
                    value={dataRow.multiChoiceValue ?? undefined}
                    onChange={onChangeOption}
                    options={
                        <>
                            {renderMultiChoiceOptions()}
                        </>
                    }
                />
            </Col>
            <Col md={3}>
                <Button
                    className={"me-2"}
                    onClick={() => setShowCreateNewMultiChoiceOptionModal(true)}>
                    Ny Lista
                </Button>
                <Button
                    disabled={!selectedOption}
                    onClick={() => setShowDeleteOptionModal(true)}>
                    Radera
                </Button>
            </Col>
        </Row>

        <Row className={"mt-3"}>
            <Col md={3}>Visa som</Col>
            <Col md={4}>
                <CustomSelect
                    isEditMode={true}
                    name={"multiChoiceDisplayType"}
                    value={dataRow.multiChoiceDisplayType}
                    onChange={onChange}
                    options={<>
                        <option value={""}>Välj här</option>
                        <option value={MultiChoiceDisplayType.DROPDOWN}
                                key={MultiChoiceDisplayType.DROPDOWN}>Dropdown
                        </option>
                        <option value={MultiChoiceDisplayType.RADIO} key={MultiChoiceDisplayType.RADIO}>Radio
                        </option>
                    </>
                    }

                />
            </Col>
        </Row>

        <When condition={!!dataRow.multiChoiceAlternatives}>
            <Row className={"mt-3"}>
                <Col md={3} className={"pt-2"}>Specificera</Col>
                <Col md={9} className={"mt-3"}>
                    <p>Välj värde för flervalsalternativ:</p>
                    <Table striped bordered responsive>
                        <thead>
                        <tr>
                            <th>Alternativ</th>
                            <th>Klassning</th>
                        </tr>
                        </thead>
                        <tbody>
                        {renderValues()}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </When>

        <Row className={"mt-3"}>
            <Col md={3}></Col>
            <Col md={4}>
                <Button
                    className={"me-3"}
                    disabled={!dataRow.header || !dataRow.multiChoiceDisplayType || !dataRow.multiChoiceAlternatives || dataRow.multiChoiceAlternatives.some(opt => opt.classifiedAs === null)}
                    onClick={handleOnAdd}>Infoga</Button>
                <Button onClick={onCancel}>Avbryt</Button>
            </Col>
        </Row>

        <When condition={showCreateNewMultiChoiceOptionModal}>
            <CreateNewDataRowMultiChoiceOption
                dataRowMultiChoiceType={DataRowMultiChoiceType.CHECKLIST}
                closeCreateNewMultiChoiceOptionModal={() => setShowCreateNewMultiChoiceOptionModal(false)}
            />
        </When>

        <When condition={showDeleteOptionModal}>
            <SimpleConfirmModal
                centered={false}
                show={true}
                header={<>Radera Alternativ</>}
                text={<>
                    Är du säker på att du vill radera "{selectedOption?.placeholder}"? <br/>
                    Detta val går inte att ångra.
                </>}
                onClickOKText={"Ja, Radera"}
                onClickOK={handleDeleteOption}
                onClickCancelText={"Avbryt"}
                onClickCancel={() => setShowDeleteOptionModal(false)}
            />
        </When>
    </>
};

export default AddMultiChoice;